<template>
    <a-spin :spinning="spinning" tip="Loading...">
        <div class="card mb-3">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-md-6">
                        <base-input alternative="" label="Site">
                            <a-select mode="multiple" show-search allowClear placeholder="All Sites" style="width: 100%" :value="selectedSites" @change="selectSites" :filter-option="(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0">
                                <a-select-option v-for="site in sites" :key="site.id" :value="site.id">
                                    {{ site.name }}
                                </a-select-option>
                            </a-select>
                        </base-input>
                    </div>
                    <div class="col-md-6">
                        <base-input alternative="" label="Terminal">
                            <a-select mode="multiple" allowClear label-in-value :value="selectedTerminals" style="width: 100%" placeholder="All Terminals" :filter-option="false" :not-found-content="fetching ? undefined : null" @search="searchTerminals" @change="selectTerminals" :disabled="!this.selectedSites.length">
                                <a-spin v-if="fetching" slot="notFoundContent" size="small" />
                                <a-select-option v-for="t in terminals" :key="t.id">
                                    {{ t.name }}
                                </a-select-option>
                            </a-select>
                        </base-input>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-12 d-flex justify-content-end">
                    <button class="btn btn-sm btn-success" style="border-radius:24px;width:25%" @click.prevent="generateExcel">
                        Generate Report in Excel
                    </button>
                    </div>
                </div>
            </div>
        </div>
    </a-spin>
</template>

<script>
    import HTTPRequest from '../../controllers/httpRequest.js'
    import secureStorage from '../../controllers/secureStorage'
    import reportUtils from '../../components/reportUtils'
    import moment from 'moment'
    import * as ExcelJS from "exceljs"
    import { saveAs } from "file-saver"
    import { mapState } from 'vuex'
    
    export default {
        data() {
            return {
                spinning: false,
                fetching: false,
                sites: [],
                selectedSites: [],
                terminals: [],
                selectedTerminals: [],
                reportData: []
            }
        },
        computed: {
            ...mapState(['endPoint'])
        },
        mounted() {
            this.loadSites()
        },
        methods: {
            moment,
            async loadSites() {
                try {
                const res = await HTTPRequest.getRequest(this.endPoint + 'sites', null)
                this.sites = res.data || []
                } catch (e) {
                console.error(e)
                }
            },
            async loadTerminals(search = '') {
                if (!this.selectedSites.length) {
                    this.terminals = []
                    return
                }

                this.fetching = true
                try {
                    const siteParam = this.selectedSites.join(',')
                    const res = await HTTPRequest.getRequest(
                        this.endPoint + `terminals?site_id=${siteParam}&search=${search}`,  
                        null
                    )
                    this.terminals = res.data || []
                } catch (e) {
                    console.error(e)
                }
                this.fetching = false
            },
            selectSites(values) {
                this.selectedSites = values
                this.selectedTerminals = []
                this.loadTerminals()
            },
            searchTerminals(value) {                                                                
                this.loadTerminals(value)
            },
            selectTerminals(value) {
                this.selectedTerminals = value
            },
            async generateExcel() {
                this.spinning = true
                this.reportData = []

                try {
                    const params = {}
                    if (this.selectedTerminals.length > 0) {
                        params.terminal_id = this.selectedTerminals.map(t => t.key).join(',')
                    }
                    if (this.selectedSites.length > 0) {
                        params.site_id = this.selectedSites.join(',')
                    }

                    const response = await HTTPRequest.getRequest(
                        this.endPoint + 'pos_site_terminal_reports',
                        params
                    )

                    if (response.length && response[0].report_details.length) {
                        this.reportData = response[0].report_details
                        await this.createExcelReport()
                    } else {
                        reportUtils.openNotificationWithIcon(this, 'warning', 'No Data.')
                    }
                } catch (e) {
                    reportUtils.openNotificationWithIcon(this, 'error', 'Failed to generate report.')
                    console.error(e)
                }

                this.spinning = false
            },
            async createExcelReport() {
                const workbook = new ExcelJS.Workbook()
                const sheet = workbook.addWorksheet("POS Site & Terminal Report")
                const startRow = 10
                let selectedSiteLabel = "All Sites"

                if (this.selectedSites.length > 0) {
                    const selectedSiteNames = this.sites.filter(site => this.selectedSites.includes(site.id)).map(site => site.name)
                    selectedSiteLabel = selectedSiteNames.join(", ")
                }

                let selectedTerminalLabel = "All Terminals"

                if (this.selectedTerminals.length > 0) {
                    const selectedTerminalIds = this.selectedTerminals.map(t => t.key)
                    const selectedTerminalNames = this.terminals.filter(t => selectedTerminalIds.includes(t.id)).map(t => t.name)
                    selectedTerminalLabel = selectedTerminalNames.join(", ")
                }

                sheet.getRow(4).values = ["POS Site and Terminal Report"]
                sheet.getRow(4).font = { bold: true, size: 14 }
                sheet.getRow(5).values = [`Generated on: ${moment().format('MM/DD/YYYY hh:mm A')}`]
                sheet.getRow(6).values = [`Generated by: ${secureStorage.getItem("email") || 'System'}`]
                sheet.getRow(7).values = [`Selected Site(s): ${selectedSiteLabel}`]
                sheet.getRow(8).values = [`Selected Terminal(s): ${selectedTerminalLabel}`]
                sheet.getRow(startRow).values = [
                    "COMPANY OWNER",
                    "STORE CONCEPT",
                    "MALL NAME",
                    "POS TYPE",
                    "MULTI POS IN 1 SITE",
                    "TERMINAL CODE",
                    "TERMINAL NAME",
                    "SITE NAME",
                    "SITE CODE",
                    "CHANNEL TYPE",
                    "MUNICIPALITY",
                    "AREA",
                    "MALL TYPE",
                    "SERIAL NUMBER",
                    "TRADE NAME",
                    "COMPANY",
                    "ADDRESS",
                    "TIN",
                    "STORE OPENING DATE",
                    "PTUSM ISSUE DATE",
                    "PERMIT NUMBER",
                    "MACHINE NUMBER",
                ]
                sheet.getRow(startRow).font = { bold: true, size: 12 }
                sheet.columns = [
                    { key: "company_owner", width: 20 },
                    { key: "site_concept", width: 20 },
                    { key: "mall_name", width: 35 },
                    { key: "pos_type", width: 20 },
                    { key: "multi_pos_in_site", width: 25 },
                    { key: "terminal_code", width: 20 },
                    { key: "terminal_name", width: 20 },
                    { key: "site_name", width: 40 },
                    { key: "site_code", width: 15 },
                    { key: "channel_type", width: 20 },
                    { key: "municipality", width: 20 },
                    { key: "area", width: 20 },
                    { key: "mall_type", width: 20 },
                    { key: "serial_number", width: 25 },
                    { key: "trade_name", width: 30 },
                    { key: "company", width: 30 },
                    { key: "address", width: 40 },
                    { key: "tin", width: 20 },
                    { key: "opening_date", width: 25 },
                    { key: "ptusm_issue_date", width: 25 },
                    { key: "permit_number", width: 20 },
                    { key: "machine_number", width: 25 },
                ]

                this.reportData.forEach(row => sheet.addRow(row))

                const buffer = await workbook.xlsx.writeBuffer()
                saveAs(
                    new Blob([buffer]),
                    `POS_Site_Terminal_Report_${moment().format('YYYYMMDD_HHmmss')}.xlsx`
                )
            }
        }
    }
</script>

<style></style>